import React, { useState, useEffect, useCallback } from "react"
import { useRouter } from "next/router"
import { useSelector, useDispatch } from "react-redux"
import {
  Box,
  Button,
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  Input,
  Paper,
  Link,
  Typography,
  InputAdornment,
  IconButton,
  ButtonGroup,
} from "@mui/material"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import Layout from "@/components/layouts/Layout"
import { register, login } from "@/store/slice/auth"
import { enqueueSnackbar } from "@/store/slice/snackbar"
import { isValidEmail } from "@/utils/email"
import { isPasswordValid } from "@/utils/password"
import PasswordRules from "@/components/PasswordRules"
import Slider from "react-slick"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import Image from "next/legacy/image"
import useUTM from "@/hooks/useUTM"

const REGISTER_BANNERS = [
  {
    title: "免費註冊，快速建立醫師及院所資訊主頁",
    image: "/admin/register-banner-01.svg",
    alt: "免費註冊，快速建立醫師及院所資訊主頁",
  },
  {
    title: "更快累積口碑好評，吸引更多新客前往",
    image: "/admin/register-banner-02.svg",
    alt: "更快累積口碑好評，吸引更多新客前往",
  },
  {
    title: "整合各大 HIS，民眾約診輕鬆數位化",
    image: "/admin/register-banner-03.svg",
    alt: "整合各大 HIS，民眾約診輕鬆數位化",
  },
]

const Register = () => {
  const router = useRouter()
  const {
    utm_source: utmSource,
    utm_campaign: utmCampaign,
    utm_medium: utmMedium,
    utm_content: utmContent,
    utm_term: utmTerm,
  } = useUTM()

  const [inputs, setInputs] = useState({
    email: "",
    password: "",
  })
  const { email, password } = inputs
  const [submitted, setSubmitted] = useState(false)
  const [passwordShown, setPasswordShown] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [errInput, setErrInput] = useState({})
  const checkValidation = useCallback(() => {
    const errInput = {}
    if (!isValidEmail(inputs.email)) {
      errInput["email"] = "Email格式不正確"
    }
    if (inputs.password.length === 0) {
      errInput["password"] = "請輸入密碼"
    } else if (!isPasswordValid(inputs.password, inputs.email)) {
      errInput["password"] = "密碼不符合規則"
    }
    setErrInput(errInput)
    return errInput
  }, [inputs])

  const { isLoggedIn } = useSelector(state => state.auth)
  const dispatch = useDispatch()

  const handleChange = e => {
    const { type, name, value, checked } = e.target
    if (type === "checkbox") {
      setInputs(inputs => ({ ...inputs, [name]: checked }))
    } else {
      setInputs(inputs => ({ ...inputs, [name]: value }))
    }
  }

  const togglePasswordVisibility = () => {
    setPasswordShown(passwordShown ? false : true)
  }

  const handleSubmit = e => {
    e.preventDefault()
    setSubmitted(true)
    setHasSubmitted(true)

    const errInput = checkValidation()
    if (Object.keys(errInput).length === 0) {
      dispatch(
        register({ email, password, utmSource, utmCampaign, utmMedium, utmContent, utmTerm })
      )
        .unwrap()
        .then(() => {
          // const returnUrl = router.query.returnUrl || "/home"
          // router.push(returnUrl)
          dispatch(
            enqueueSnackbar({
              message: "註冊成功！登入中...",
              options: {
                variant: "success",
              },
            })
          )
          // return
          dispatch(login({ username: email, password }))
            .unwrap()
            .then(() => {
              router.push({
                pathname: "/account/register-welcome",
              })
            })
        })
        .catch(error => {
          console.log("Register page", error)
          setSubmitted(false)

          let message = "註冊失敗！"
          if (error === "user already exists") {
            message += "此帳號已註冊。"
          }

          dispatch(
            enqueueSnackbar({
              message,
              options: {
                variant: "error",
              },
            })
          )
        })
    } else {
      setSubmitted(false)
    }
  }

  const SliderSettings = {
    dots: true,
    fade: true,
    lazyLoad: true,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 2,
    autoplay: true,
    autoplaySpeed: 3500,
    pauseOnHover: true,
  }

  useEffect(() => {
    if (Object.keys(errInput).length !== 0) {
      checkValidation(inputs)
    }
  }, [inputs]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // redirect to home if already logged in
    console.log(`isLoggedIn`, isLoggedIn)
    if (isLoggedIn) {
      router.push("/home")
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <Grid container component="main" sx={{ height: "calc(100vh - 120px)" }}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={6}
          xl={6}
          sx={{
            // display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 120px)",
            display: { xs: "none", lg: "flex" },
          }}
        >
          <Typography
            component="h1"
            variant="headingH3Heavy"
            sx={{ marginBottom: "25px", color: "SHADES_900" }}
          >
            <Typography
              component="span"
              variant="headingH4Heavy"
              sx={{
                color: "PRIMARY_600",
              }}
            >
              加入PinMed
            </Typography>
            <br />
            讓流量成為走入診間的顧客
          </Typography>

          <Box
            sx={{
              margin: "0 auto",
              width: { lg: "85%", xl: "80%" },
              textAlign: "center",
              position: "relative",
            }}
          >
            <Slider {...SliderSettings}>
              {REGISTER_BANNERS.map((data, index) => (
                <div key={index}>
                  <Image alt={data.alt} src={data.image} width={375} height={334} />
                  <Typography
                    component="h2"
                    variant="headingH5Medium"
                    sx={{
                      color: "SHADES_500",
                      marginTop: "20px",
                      mb: "20px",
                    }}
                  >
                    {data.title}
                  </Typography>
                </div>
              ))}
            </Slider>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={6}
          xl={6}
          sx={{
            m: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "SHADES_100",
            py: "50px",
            height: "calc(100vh - 112px)",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Typography
            component="h2"
            variant="headingH3Heavy"
            sx={{
              mb: { xs: "0px", md: "25px" },
              color: "SHADES_900",
              display: { xs: "block", lg: "none" },
              fontSize: { xs: "33px", md: "40px" },
              lineHeight: "42.9px",
            }}
          >
            <Typography
              component="span"
              variant="headingH4Heavy"
              sx={{
                color: "PRIMARY_600",
                fontSize: { xs: "28px", md: "33px" },
              }}
            >
              加入PinMed
            </Typography>
            <br />
            讓流量成為走入診間的顧客
          </Typography>

          <ButtonGroup
            variant="outlined"
            aria-label="outlined button group"
            disableElevation
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "480px",
              width: "100%",
              mt: { xs: 2, sm: 4 },
            }}
          >
            <Button
              variant="contained"
              component={Link}
              underline="none"
              href={"/account/login"}
              fullWidth
              sx={{
                color: "PRIMARY_600",
                backgroundColor: "primary.contrastText",
                overflow: "hidden",
                "&:hover": {
                  color: "primary.contrastText",
                  backgroundColor: "PRIMARY_600",
                },
              }}
            >
              登入
            </Button>
            <Button
              variant="outlined"
              component={Link}
              underline="none"
              href={"/account/register"}
              fullWidth
              sx={{
                color: "primary.contrastText",
                backgroundColor: "PRIMARY_600",
                overflow: "hidden",
                "&:hover": {
                  color: "primary.contrastText",
                  backgroundColor: "PRIMARY_600",
                },
              }}
            >
              註冊
            </Button>
          </ButtonGroup>

          <Paper
            variant="outlined"
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ my: { xs: 2, sm: 4 }, p: { xs: 3, sm: 5 }, maxWidth: "480px" }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl required error={!!errInput["email"]}>
                  <InputLabel htmlFor="email">帳號</InputLabel>
                  <Input
                    autoFocus
                    id="email"
                    name="email"
                    onChange={handleChange}
                    value={email}
                    placeholder="請輸入Email"
                  />
                  {errInput["email"] && <FormHelperText>{errInput["email"]}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl required error={!!errInput["password"]}>
                  <InputLabel htmlFor="password">密碼</InputLabel>
                  <Input
                    id="password"
                    name="password"
                    type={passwordShown ? "text" : "password"}
                    onChange={handleChange}
                    value={password}
                    autoComplete="new-password"
                    placeholder="請區分大小寫"
                    endAdornment={
                      <InputAdornment sx={{ position: "absolute", right: 16 }} position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility}
                          onMouseDown={event => {
                            event.preventDefault()
                          }}
                          edge="end"
                        >
                          {passwordShown ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {errInput["password"] && <FormHelperText>{errInput["password"]}</FormHelperText>}
                </FormControl>
                <PasswordRules password={password} account={email} showErrors={hasSubmitted} />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, backgroundColor: "PRIMARY_600" }}
              disabled={submitted}
            >
              {submitted ? "驗證中..." : "免費註冊"}
            </Button>
            <div>
              註冊即表示您已閱讀並同意PinMed
              <Link
                underline="none"
                href="https://pinmed.co/terms-of-use"
                target="_blank"
                sx={{ color: "PRIMARY_600" }}
              >
                使用者條款
              </Link>
            </div>
          </Paper>
        </Grid>
      </Grid>
    </Layout>
  )
}

export default Register
